/* eslint-disable @typescript-eslint/no-explicit-any */
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { InputNumber } from 'primereact/inputnumber';
import { Controller, useForm, useFieldArray } from 'react-hook-form';
import { z } from 'zod';

import { Toast } from 'sharedUi/Toast';
import { Button } from 'sharedUi/Button';
import { Input } from 'sharedUi/Input';
import { TextArea } from 'sharedUi/TextArea';
import { useBreadcrumbTitle } from 'sharedUi/BreadcrumbContext';
import { useSecureNavigation } from '../../hooks/useSecureNavigation';
import { useNavigationBlocker } from '../../hooks/useNavigationBlocker';
import DiscardChangesDialog from '../../components/dialogs/discard-changes-dialog';
import { EmptyState, PageSkeleton, SectionLabel } from '../../components/common/panel';
import ScreenHeader from '../../components/common/screen-header';
import { PANEL, PRETTY, riseAt } from '../../components/common/ui-kit';
import FormSelect from '../../components/forms/form-select';
import { useCanUpdate } from '../../hooks/usePermissions';
import { JOB_NAMES } from '../../constants/jobNames';

import { agentsService } from '../../services/agents.service';
import { aiModelsService } from '../../services/ai-models.service';
import { promptsService } from '../../services/prompts.service';
import { ragsService } from '../../services/rags.service';
import { projectsService } from '../../services/projects.service';
import { extractErrorMessage } from '../../utils/error-handler';

const STATUS_OPTIONS = [
  { label: 'Active', value: 'ACTIVE' },
  { label: 'Inactive', value: 'INACTIVE' },
];

/** Reference-free online-eval metrics — the only ones a live turn can be scored on. */
const ONLINE_EVAL_METRICS = ['ANSWER_RELEVANCY', 'TASK_COMPLETION', 'GROUNDEDNESS', 'CITATION_CORRECTNESS'];

/** The default a new tool starts from: a valid, empty JSON Schema object. */
const EMPTY_PARAMS = JSON.stringify({ type: 'object', properties: {}, required: [] }, null, 2);

/** Providers agree on letters, digits and underscore; anything else is rejected
 *  by at least one function-calling API, so it is refused here rather than at
 *  run time when every case would error. */
const TOOL_NAME = /^[A-Za-z0-9_.-]{1,64}$/;

/** How the catalogue is offered. AUTO is right for a first agent, which is why
 *  the whole group is collapsed — a forced choice gets cargo-culted otherwise. */
const TOOL_CHOICE_OPTIONS = [
  { label: 'Auto — the model decides', value: 'AUTO' },
  { label: 'Require any tool', value: 'ANY' },
  { label: 'Require one specific tool', value: 'TOOL' },
  { label: 'Forbid tools', value: 'NONE' },
];

const toolSchema = z.object({
  name: z.string().min(1, 'Tool name is required').regex(TOOL_NAME, 'Letters, digits and underscore only'),
  description: z.string().optional().or(z.literal('')),
  parameters: z.string().refine((v) => {
    if (!v || !v.trim()) return true;
    try {
      const parsed = JSON.parse(v);
      return !!parsed && typeof parsed === 'object' && !Array.isArray(parsed);
    } catch { return false; }
  }, 'Must be a JSON object (JSON Schema)'),
});

const schema = z.object({
  agentCode: z.string().min(2, 'Agent key is required').max(100)
    .regex(/^[A-Za-z0-9_.-]+$/, 'Letters, digits, underscore, dot and hyphen only'),
  name: z.string().min(1, 'Name is required').max(200),
  description: z.string().optional().or(z.literal('')),
  status: z.string(),
  systemPrompt: z.string().optional().or(z.literal('')),
  modelRef: z.string().optional().or(z.literal('')),
  promptRef: z.string().optional().or(z.literal('')),
  // '' = the prompt's active version; otherwise a version number as text.
  promptVersion: z.string().optional().or(z.literal('')),
  appId: z.string().optional().or(z.literal('')),
  tools: z.array(toolSchema),
  // A chat agent may answer without tools; the backend allows it too.
  conversational: z.boolean(),
  historyMessages: z.number().int().min(2).max(200).nullable(),
  rewriteWindow: z.number().int().min(1).max(50).nullable(),
  maxSummaryChars: z.number().int().min(500).max(50000).nullable(),
  toolChoiceMode: z.string(),
  toolChoiceToolName: z.string().optional().or(z.literal('')),
  disableParallelToolUse: z.boolean(),
  modelParams: z.string().refine((v) => {
    if (!v || !v.trim()) return true;
    try {
      const parsed = JSON.parse(v);
      return !!parsed && typeof parsed === 'object' && !Array.isArray(parsed);
    } catch { return false; }
  }, 'Must be a JSON object, e.g. {"temperature": 0.2}'),
}).refine((d) => d.conversational || d.tools.length >= 1, {
  message: 'Add at least one tool — an agent with none cannot be graded',
  path: ['tools'],
  // Conversation (chat) configuration. Optional; an agent without it is unchanged.
  conversationEnabled: z.boolean(),
  historyMessages: z.string().optional().or(z.literal('')),
  summarize: z.boolean(),
  rewriteQuery: z.boolean(),
  convMaxTokens: z.string().optional().or(z.literal('')),
  ragCode: z.string().optional().or(z.literal('')),
  ragVersion: z.string().optional().or(z.literal('')),
  promptRef: z.string().optional().or(z.literal('')),
  onlineEvalEnabled: z.boolean(),
  sampleRate: z.string().optional().or(z.literal('')),
  metrics: z.array(z.string()),
  judgeModelRef: z.string().optional().or(z.literal('')),
  // Chat UI presentation (conversation.ui) — how this co-pilot looks in the chat.
  uiTitle: z.string().optional().or(z.literal('')),
  uiIcon: z.string().optional().or(z.literal('')),
  uiAccent: z.string().optional().or(z.literal('')),
  uiWelcomeTitle: z.string().optional().or(z.literal('')),
  uiWelcomeText: z.string().optional().or(z.literal('')),
  uiStarters: z.string().optional().or(z.literal('')),
  uiShowSources: z.boolean(),
  uiShowFeedback: z.boolean(),
}).refine((d) => d.toolChoiceMode !== 'TOOL' || !!d.toolChoiceToolName, {
  message: 'Pick which tool the model must call',
  path: ['toolChoiceToolName'],
}).refine((d) => !d.modelParams?.trim() || !!d.modelRef, {
  // Without a pinned model the run's own client answers and its settings apply,
  // so these would be silently ignored — the backend refuses it too.
  message: 'Sampling settings need an answer model',
  path: ['modelParams'],
}).refine((d) => d.conversationEnabled || (d.tools?.length ?? 0) >= 1, {
  // A graded agent needs a tool catalogue; a chat agent may have none.
  message: 'Add at least one tool — an agent with none cannot be graded (or enable Conversation).',
  path: ['tools'],
});

type FormData = z.infer<typeof schema>;

const AgentForm = () => {
  const { id, isReady, navigateToList } = useSecureNavigation({ entity: 'agent', basePath: '/agents' });
  const isEditMode = Boolean(id);
  const queryClient = useQueryClient();
  const toast = useRef<Toast>(null);
  const canEdit = useCanUpdate(JOB_NAMES.AGENT);

  const { control, handleSubmit, reset, watch, setValue, formState: { errors, isDirty } } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      agentCode: '', name: '', description: '', status: 'ACTIVE',
      systemPrompt: '', modelRef: '', promptRef: '', promptVersion: '', appId: '',
      tools: [{ name: '', description: '', parameters: EMPTY_PARAMS }],
      toolChoiceMode: 'AUTO', toolChoiceToolName: '', disableParallelToolUse: false,
      modelParams: '',
      conversationEnabled: false, historyMessages: '', summarize: true, rewriteQuery: true,
      convMaxTokens: '', ragCode: '', ragVersion: '', promptRef: '',
      onlineEvalEnabled: false, sampleRate: '', metrics: [], judgeModelRef: '',
      uiTitle: '', uiIcon: '', uiAccent: '', uiWelcomeTitle: '', uiWelcomeText: '', uiStarters: '',
      uiShowSources: true, uiShowFeedback: true,
      conversational: false, historyMessages: null, rewriteWindow: null, maxSummaryChars: null,
    },
    mode: 'onChange',
  });

  const { fields, append, remove } = useFieldArray({ control, name: 'tools' });
  const toolChoiceMode = watch('toolChoiceMode');
  const conversationEnabled = watch('conversationEnabled');
  const onlineEvalEnabled = watch('onlineEvalEnabled');
  // Forcing a tool can only name one this agent offers — the backend enforces the
  // same rule, so a dropdown here prevents a 422 rather than duplicating it.
  const watchedTools = watch('tools');
  const toolNameOptions = (watchedTools || [])
    .map((t) => (t?.name || '').trim())
    .filter(Boolean)
    .map((name) => ({ value: name, label: name }));

  // A chat agent needs no tools; when chat is turned on, drop the default blank
  // rows so the form validates without the author deleting them by hand.
  useEffect(() => {
    if (!conversationEnabled) return;
    for (let i = (watchedTools?.length ?? 0) - 1; i >= 0; i--) {
      const t = watchedTools?.[i];
      if (!(t?.name || '').trim() && !(t?.description || '').trim()) remove(i);
    }
  }, [conversationEnabled]); // eslint-disable-line react-hooks/exhaustive-deps

  const { data: accessibleProjects = [] } = useQuery({ queryKey: ['projects', 'accessible'], queryFn: projectsService.getAccessible, staleTime: 5 * 60 * 1000 });
  const projectOptions = accessibleProjects.map((p) => ({ value: p._id, label: p.appCode ? `${p.name} (${p.appCode})` : p.name }));
  const projectLocked = accessibleProjects.length <= 1;
  const selectedAppId = watch('appId');
  useEffect(() => { if (!isEditMode && !selectedAppId && accessibleProjects.length) setValue('appId', accessibleProjects[0]._id); }, [isEditMode, selectedAppId, accessibleProjects, setValue]);

  // runnable_only: an agent calls its model with prompts, so an embedding or
  // document-analysis model here could only ever be a choice that fails. Scoped to
  // the selected project (server intersects with globals), so the list reflects it.
  const { data: modelsResp } = useQuery({ queryKey: ['ai-models', 'runnable', selectedAppId || 'all'], queryFn: () => aiModelsService.list({ page: 1, page_size: 100, runnable_only: true, app_id: selectedAppId || undefined }), staleTime: 5 * 60 * 1000 });
  const modelOptions = (modelsResp?.data || []).map((m: any) => ({ value: m._id, label: `${m.displayName} · ${m.providerType}` }));

  // Knowledge bases (RAGs) for the selected project; the chat agent's ragRef picks one.
  const { data: ragsResp } = useQuery({ queryKey: ['rags', 'for-agent', selectedAppId || 'all'], queryFn: () => ragsService.list({ page: 1, page_size: 200, app_id: selectedAppId || undefined }), staleTime: 5 * 60 * 1000 });
  const ragOptions = (ragsResp?.data || []).map((r: any) => ({ value: r.ragCode, label: r.name ? `${r.ragCode} · ${r.name}` : r.ragCode }));

  // A stored prompt layered onto the system prompt, optionally pinned to one version.
  const { data: promptsResp } = useQuery({ queryKey: ['prompts', 'names'], queryFn: () => promptsService.list({ page: 1, page_size: 100 }), staleTime: 60 * 1000 });
  const promptOptions = ((promptsResp as { data?: { _id: string; name: string; type?: string }[] } | undefined)?.data || [])
    .map((p) => ({ value: p._id, label: `${p.name}${p.type ? ` (${p.type})` : ''}` }));
  const linkedPrompt = watch('promptRef');
  const { data: linkedVersions = [] } = useQuery({
    queryKey: ['prompt-versions', linkedPrompt],
    queryFn: () => promptsService.listVersions(linkedPrompt!),
    enabled: !!linkedPrompt,
    staleTime: 60 * 1000,
    retry: false,
  });
  const promptVersionOptions = [
    { value: '', label: 'Whichever version is active' },
    ...linkedVersions.filter((v) => v.status !== 'DISCARDED')
      .map((v) => ({ value: String(v.version), label: `v${v.version} · ${v.status}${v.tags?.length ? ` · ${v.tags.join(', ')}` : ''}` })),
  ];

  const { data: agent, isLoading } = useQuery({ queryKey: ['agents', id], queryFn: () => agentsService.getById(id!), enabled: isEditMode && isReady && !!id });
  useBreadcrumbTitle(isEditMode ? agent?.name : null);

  useEffect(() => {
    if (agent && isEditMode) {
      reset({
        agentCode: agent.agentCode || '',
        name: agent.name || '',
        description: agent.description || '',
        status: agent.status || 'ACTIVE',
        systemPrompt: agent.systemPrompt || '',
        modelRef: agent.modelRef || '',
        promptRef: agent.promptRef || '',
        promptVersion: agent.promptVersion ? String(agent.promptVersion) : '',
        appId: agent.appId || '',
        toolChoiceMode: agent.toolChoice?.mode || 'AUTO',
        toolChoiceToolName: agent.toolChoice?.toolName || '',
        disableParallelToolUse: !!agent.toolChoice?.disableParallelToolUse,
        modelParams: agent.modelParams ? JSON.stringify(agent.modelParams, null, 2) : '',
        conversational: !!agent.conversation,
        historyMessages: agent.conversation?.historyMessages ?? 20,
        rewriteWindow: agent.conversation?.rewriteWindow ?? 6,
        maxSummaryChars: agent.conversation?.maxSummaryChars ?? 8000,
        // A chat agent with no tools stays at none, rather than gaining a blank one it cannot save.
        tools: (agent.tools?.length ? agent.tools : agent.conversation ? [] : [{ name: '', description: '', parameters: undefined }]).map((t: any) => ({
          name: t.name || '',
          description: t.description || '',
          // Stored as an object, edited as text — the schema is JSON either way.
          parameters: t.parameters ? JSON.stringify(t.parameters, null, 2) : EMPTY_PARAMS,
        })),
        conversationEnabled: !!agent.conversation?.enabled,
        historyMessages: agent.conversation?.historyMessages != null ? String(agent.conversation.historyMessages) : '',
        summarize: agent.conversation?.summarize ?? true,
        rewriteQuery: agent.conversation?.rewriteQuery ?? true,
        convMaxTokens: agent.conversation?.maxTokens != null ? String(agent.conversation.maxTokens) : '',
        ragCode: agent.ragRef?.ragCode || '',
        ragVersion: agent.ragRef?.version != null ? String(agent.ragRef.version) : '',
        promptRef: agent.promptRef || '',
        onlineEvalEnabled: !!agent.onlineEval?.enabled,
        sampleRate: agent.onlineEval?.sampleRate != null ? String(agent.onlineEval.sampleRate) : '',
        metrics: agent.onlineEval?.metrics || [],
        judgeModelRef: agent.onlineEval?.judgeModelRef || '',
        uiTitle: agent.conversation?.ui?.title || '',
        uiIcon: agent.conversation?.ui?.icon || '',
        uiAccent: agent.conversation?.ui?.accentColor || '',
        uiWelcomeTitle: agent.conversation?.ui?.welcomeTitle || '',
        uiWelcomeText: agent.conversation?.ui?.welcomeText || '',
        uiStarters: (agent.conversation?.ui?.starters || []).join('\n'),
        uiShowSources: agent.conversation?.ui?.showSources !== false,
        uiShowFeedback: agent.conversation?.ui?.showFeedback !== false,
      });
    }
  }, [agent, isEditMode, reset]);

  const mutation = useMutation({
    mutationFn: (data: any) => (isEditMode && id ? agentsService.update(id, data) : agentsService.create(data)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['agents'] });
      toast.current?.show({ severity: 'success', summary: 'Saved', detail: `Agent ${isEditMode ? 'updated' : 'created'}`, life: 3000 });
      setTimeout(() => navigateToList(), 800);
    },
    onError: (error: any) => toast.current?.show({ severity: 'error', summary: 'Error', detail: extractErrorMessage(error, 'Failed to save the agent'), life: 10000 }),
  });

  const onSubmit = (d: FormData) => {
    const payload: any = {
      agentCode: d.agentCode.trim(),
      name: d.name,
      description: d.description || null,
      status: d.status,
      systemPrompt: d.systemPrompt || null,
      modelRef: d.modelRef || null,
      promptRef: d.promptRef || null,
      promptVersion: d.promptRef && d.promptVersion ? Number(d.promptVersion) : null,
      tools: d.tools.map((t) => ({
        name: t.name.trim(),
        description: t.description || null,
        parameters: t.parameters && t.parameters.trim() ? JSON.parse(t.parameters) : undefined,
      })),
    };
    // AUTO is stored as absent: nothing to backfill on existing rows, and the
    // executor omits the argument entirely so an older client still works.
    payload.toolChoice = d.toolChoiceMode === 'AUTO' && !d.disableParallelToolUse
      ? null
      : {
          mode: d.toolChoiceMode,
          toolName: d.toolChoiceMode === 'TOOL' ? d.toolChoiceToolName : null,
          disableParallelToolUse: d.disableParallelToolUse,
        };
    payload.modelParams = d.modelParams?.trim() ? JSON.parse(d.modelParams) : null;
    // The rest of the conversation block (prompt overrides, switches) is kept as stored.
    if (d.conversational && agent?.conversation) {
      payload.conversation = { ...agent.conversation, historyMessages: d.historyMessages,
        rewriteWindow: d.rewriteWindow, maxSummaryChars: d.maxSummaryChars };
    }
    if (!isEditMode && d.appId) payload.appId = d.appId;

    // Conversation (chat) config. Null when disabled, so a non-chat agent is unchanged.
    const num = (v?: string) => (v && v.trim() ? Number(v) : undefined);
    payload.conversation = d.conversationEnabled
      ? {
          enabled: true,
          historyMessages: num(d.historyMessages),
          summarize: d.summarize,
          rewriteQuery: d.rewriteQuery,
          maxTokens: num(d.convMaxTokens),
          ui: {
            title: d.uiTitle?.trim() || null,
            icon: d.uiIcon?.trim() || null,
            accentColor: d.uiAccent?.trim() || null,
            welcomeTitle: d.uiWelcomeTitle?.trim() || null,
            welcomeText: d.uiWelcomeText?.trim() || null,
            starters: d.uiStarters?.trim() ? d.uiStarters.split('\n').map((s) => s.trim()).filter(Boolean) : null,
            showSources: d.uiShowSources,
            showFeedback: d.uiShowFeedback,
          },
        }
      : null;
    payload.ragRef = d.ragCode?.trim() ? { ragCode: d.ragCode.trim(), version: num(d.ragVersion) ?? null } : null;
    payload.promptRef = d.promptRef?.trim() || null;
    payload.onlineEval = d.onlineEvalEnabled
      ? {
          enabled: true,
          sampleRate: num(d.sampleRate) ?? 0.1,
          metrics: d.metrics?.length ? d.metrics : null,
          judgeModelRef: d.judgeModelRef?.trim() || null,
        }
      : null;
    mutation.mutate(payload);
  };

  const { showLeaveDialog, confirmLeave, cancelLeave, handleNavigate } = useNavigationBlocker({ when: isDirty });

  if (isEditMode && isLoading) return <PageSkeleton panels={2} />;

  const card = `${PANEL} ui-rise p-4`;

  return (
    <div className="p-4" data-testid="SCR-Agent-Form">
      <Toast ref={toast} position="top-right" />
      <ScreenHeader
        icon="pi pi-bolt"
        onBack={() => handleNavigate('/agents')}
        title={isEditMode ? 'Edit Agent' : 'Create Agent'}
        right={isEditMode && agent && (
          <span className="font-mono text-xs text-ink-muted">{agent.agentCode}</span>
        )}
      />

      <form onSubmit={handleSubmit(onSubmit)} className="flex w-full flex-col gap-4">
        <div className={card} style={riseAt(0)}>
          <SectionLabel icon="pi pi-id-card">Identity</SectionLabel>
          <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Controller name="agentCode" control={control} render={({ field }) => (
              <Input label="Agent Key *" {...field} value={field.value || ''} error={errors.agentCode?.message}
                placeholder="fir_desk_triage" disabled={isEditMode && !canEdit} />
            )} />
            <Controller name="name" control={control} render={({ field }) => (
              <Input label="Name *" {...field} value={field.value || ''} error={errors.name?.message} placeholder="FIR desk triage" />
            )} />
            <Controller name="status" control={control} render={({ field }) => (
              <FormSelect label="Status" name={field.name} value={field.value || 'ACTIVE'} options={STATUS_OPTIONS}
                onChange={(e: any) => field.onChange(e.target ? e.target.value : e)} />
            )} />
            <Controller name="appId" control={control} render={({ field }) => (
              <FormSelect label="Project" name={field.name} value={field.value || ''} options={projectOptions}
                onChange={(e: any) => field.onChange(e.target ? e.target.value : e)}
                disabled={isEditMode || projectLocked} filter />
            )} />
            <div className="sm:col-span-2">
              <Controller name="description" control={control} render={({ field }) => (
                <TextArea label="Description" value={field.value || ''} onChange={field.onChange} rows={2}
                  placeholder="What this agent is for" />
              )} />
            </div>
          </div>
          <p className="mt-2 max-w-prose text-xs text-ink-muted" style={PRETTY}>
            The agent key is what a suite stores. Renaming it is refused while a suite targets it.
          </p>
        </div>

        <div className={card} style={riseAt(1)}>
          <SectionLabel icon="pi pi-comment">Instructions</SectionLabel>
          <div className="mt-3 grid grid-cols-1 gap-3">
            <Controller name="systemPrompt" control={control} render={({ field }) => (
              <TextArea label="System prompt" value={field.value || ''} onChange={field.onChange} rows={8}
                placeholder="The standing instructions the model is given before each case." />
            )} />
            <Controller name="modelRef" control={control} render={({ field }) => (
              <FormSelect label="Answer model" name={field.name} value={field.value || ''} options={modelOptions}
                onChange={(e: any) => field.onChange(e.target ? e.target.value : e)} showClear filter />
            )} />
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Controller name="promptRef" control={control} render={({ field }) => (
                <FormSelect label="Linked prompt" name={field.name} value={field.value || ''} options={promptOptions}
                  onChange={(e: any) => { field.onChange((e.target ? e.target.value : e) || ''); setValue('promptVersion', '', { shouldDirty: true }); }}
                  showClear filter helperText="From Prompt Management; its roles and task text are added to the system prompt." data-testid="AgentForm.PromptRef" />
              )} />
              <Controller name="promptVersion" control={control} render={({ field }) => (
                <FormSelect label="Prompt version" name={field.name} value={field.value || ''} options={promptVersionOptions}
                  onChange={(e: any) => field.onChange((e.target ? e.target.value : e) || '')} disabled={!linkedPrompt}
                  helperText="Pin a number to freeze what this agent answers with. Changing it moves the agent's version." data-testid="AgentForm.PromptVersion" />
              )} />
            </div>
          </div>
          <p className="mt-2 max-w-prose text-xs text-ink-muted" style={PRETTY}>
            Leave the model blank to answer on whichever model the run uses. Pin one when the agent
            depends on a model that supports function calling.
          </p>
        </div>

        {watch('conversational') && (
          <div className={card} style={riseAt(2)} data-testid="AgentForm.Conversation">
            <SectionLabel icon="pi pi-comments">Conversation</SectionLabel>
            <p className="mt-1 max-w-prose text-xs text-ink-muted" style={PRETTY}>
              How this agent handles a chat. Changing any of these moves the agent to a new version.
            </p>
            <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-3">
              {([
                ['historyMessages', 'History sent each turn', 2, 200, 'Recent messages the model sees with every reply. Older ones are summarised.'],
                ['rewriteWindow', 'Messages the rewriter reads', 1, 50, 'How far back a follow-up like “and carry-over?” is resolved before searching.'],
                ['maxSummaryChars', 'Summary length cap', 500, 50000, 'Characters kept in the running summary of older turns.'],
              ] as const).map(([name, label, min, max, hint]) => (
                <Controller key={name} name={name} control={control} render={({ field }) => (
                  <div className="flex flex-col gap-1">
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300" htmlFor={name}>{label}</label>
                    <InputNumber inputId={name} value={field.value ?? null} onValueChange={(e) => field.onChange(e.value ?? null)}
                      min={min} max={max} useGrouping={false} className="w-full" />
                    <span className="text-xs text-ink-muted" style={PRETTY}>{hint} {min}–{max}.</span>
                    {errors[name]?.message && <span className="text-xs text-rose-600">{String(errors[name]?.message)}</span>}
                  </div>
                )} />
              ))}
            </div>
          </div>
        )}

        <div className={card} style={riseAt(2)}>
          <div className="flex items-start justify-between gap-3">
            <div>
              <SectionLabel icon="pi pi-wrench">Tools</SectionLabel>
              <p className="mt-1 max-w-prose text-xs text-ink-muted" style={PRETTY}>
                The catalogue every case is offered. Nothing is executed — an agent eval grades which
                of these the model reaches for.
              </p>
            </div>
            <span className="text-xs tabular-nums text-ink-muted">{fields.length}</span>
          </div>

          {errors.tools?.message && (
            <p className="mt-2 text-sm text-rose-600 dark:text-rose-400">{errors.tools.message}</p>
          )}

          <div className="mt-3 flex flex-col gap-3">
            {fields.length === 0 && (
              <EmptyState icon="pi pi-wrench" title="No tools yet"
                hint="An agent with no tools cannot be graded on tool selection." />
            )}
            {fields.map((f, index) => (
              <div key={f.id} className="ui-inset p-3">
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <Controller name={`tools.${index}.name`} control={control} render={({ field }) => (
                    <Input label="Tool name *" {...field} value={field.value || ''} error={errors.tools?.[index]?.name?.message}
                      placeholder="register_zero_fir" />
                  )} />
                  <Controller name={`tools.${index}.description`} control={control} render={({ field }) => (
                    <Input label="Description" {...field} value={field.value || ''} placeholder="When the model should reach for it" />
                  )} />
                </div>
                <div className="mt-3">
                  <Controller name={`tools.${index}.parameters`} control={control} render={({ field }) => (
                    <TextArea label="Parameters (JSON Schema)" value={field.value || ''} onChange={field.onChange}
                      rows={6} error={errors.tools?.[index]?.parameters?.message}
                      placeholder={EMPTY_PARAMS} />
                  )} />
                </div>
                <div className="mt-2 flex justify-end">
                  <Button type="button" label="Remove" icon="pi pi-trash" severity="danger" text size="small"
                    className="ui-press" onClick={() => remove(index)} />
                </div>
              </div>
            ))}
          </div>

          <div className="mt-3">
            <Button type="button" label="Add tool" icon="pi pi-plus" size="small" className="ui-press"
              onClick={() => append({ name: '', description: '', parameters: EMPTY_PARAMS })} />
          </div>
        </div>

        {/* Collapsed on purpose. Auto is correct for a first agent, and putting a
            forced choice in front of an author gets it copied into suites that
            never needed it. */}
        <details className={card} style={riseAt(3)}>
          <summary className="cursor-pointer select-none">
            <SectionLabel icon="pi pi-sliders-h">Advanced — how the tools are offered</SectionLabel>
          </summary>
          <div className="mt-3 grid grid-cols-1 gap-3">
            <Controller name="toolChoiceMode" control={control} render={({ field }) => (
              <FormSelect label="Tool selection" name={field.name} value={field.value || 'AUTO'}
                options={TOOL_CHOICE_OPTIONS}
                onChange={(e: any) => field.onChange(e.target ? e.target.value : e)} />
            )} />
            {toolChoiceMode === 'TOOL' && (
              <Controller name="toolChoiceToolName" control={control} render={({ field }) => (
                <FormSelect label="Which tool *" name={field.name} value={field.value || ''}
                  options={toolNameOptions}
                  onChange={(e: any) => field.onChange(e.target ? e.target.value : e)}
                  error={!!errors.toolChoiceToolName} helperText={errors.toolChoiceToolName?.message} />
              )} />
            )}
            <Controller name="disableParallelToolUse" control={control} render={({ field }) => (
              <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                <input type="checkbox" checked={!!field.value}
                  onChange={(e) => field.onChange(e.target.checked)} />
                At most one tool call per turn
              </label>
            )} />
            <p className="max-w-prose text-xs text-ink-muted" style={PRETTY}>
              &ldquo;Forbid tools&rdquo; is how a case tests restraint — that the model
              correctly calls nothing. &ldquo;Require any tool&rdquo; separates picking the
              right tool from deciding to act at all.
            </p>
          </div>
        </details>

        <details className={card} style={riseAt(4)}>
          <summary className="cursor-pointer select-none">
            <SectionLabel icon="pi pi-cog">Advanced — sampling</SectionLabel>
          </summary>
          <div className="mt-3">
            <Controller name="modelParams" control={control} render={({ field }) => (
              <TextArea label="Model parameters (JSON)" value={field.value || ''}
                onChange={field.onChange} rows={4}
                error={errors.modelParams?.message}
                placeholder={'{\n  "temperature": 0.2\n}'} />
            )} />
            <p className="mt-2 max-w-prose text-xs text-ink-muted" style={PRETTY}>
              Only with a pinned answer model — without one the run&rsquo;s own model answers and
              its settings apply. Names are checked at run time against that model&rsquo;s
              parameter schema, so an unsupported one errors every case rather than being ignored.
            </p>
          </div>
        </details>

        <details className={card} style={riseAt(5)}>
          <summary className="cursor-pointer select-none">
            <SectionLabel icon="pi pi-comments">Conversation (chat)</SectionLabel>
          </summary>
          <div className="mt-3 flex flex-col gap-3">
            <Controller name="conversationEnabled" control={control} render={({ field }) => (
              <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                <input type="checkbox" checked={!!field.value} onChange={(e) => field.onChange(e.target.checked)} />
                Enable this agent for multi-turn conversations
              </label>
            )} />

            {conversationEnabled && (
              <>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <Controller name="historyMessages" control={control} render={({ field }) => (
                    <Input label="History messages (2–200)" {...field} value={field.value || ''} type="number" placeholder="20" />
                  )} />
                  <Controller name="convMaxTokens" control={control} render={({ field }) => (
                    <Input label="Max answer tokens" {...field} value={field.value || ''} type="number" placeholder="model default" />
                  )} />
                </div>
                <div className="flex flex-wrap gap-4">
                  <Controller name="summarize" control={control} render={({ field }) => (
                    <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                      <input type="checkbox" checked={!!field.value} onChange={(e) => field.onChange(e.target.checked)} />
                      Summarise older turns
                    </label>
                  )} />
                  <Controller name="rewriteQuery" control={control} render={({ field }) => (
                    <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                      <input type="checkbox" checked={!!field.value} onChange={(e) => field.onChange(e.target.checked)} />
                      Rewrite follow-ups before retrieval
                    </label>
                  )} />
                </div>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <Controller name="ragCode" control={control} render={({ field }) => (
                    <FormSelect label="Knowledge base (RAG)" name={field.name} value={field.value || ''} options={ragOptions}
                      onChange={(e: any) => field.onChange(e.target ? e.target.value : e)}
                      placeholder={ragOptions.length ? 'No retrieval' : 'No RAGs in this project'} showClear filter />
                  )} />
                  <Controller name="ragVersion" control={control} render={({ field }) => (
                    <Input label="RAG version" {...field} value={field.value || ''} type="number" placeholder="active" />
                  )} />
                  <div className="sm:col-span-2">
                    <Controller name="promptRef" control={control} render={({ field }) => (
                      <Input label="Linked prompt id (promptRef)" {...field} value={field.value || ''} placeholder="optional — layered onto the system prompt" />
                    )} />
                  </div>
                </div>

                {/* Chat appearance — how this co-pilot presents in the chat UI (conversation.ui). */}
                <div className="ui-inset flex flex-col gap-3 p-3">
                  <SectionLabel icon="pi pi-palette">Chat appearance</SectionLabel>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                    <Controller name="uiTitle" control={control} render={({ field }) => (
                      <Input label="Header title" {...field} value={field.value || ''} placeholder="defaults to the agent name" />
                    )} />
                    <Controller name="uiIcon" control={control} render={({ field }) => (
                      <Input label="Icon (PrimeIcons)" {...field} value={field.value || ''} placeholder="pi pi-android" />
                    )} />
                    <Controller name="uiAccent" control={control} render={({ field }) => (
                      <Input label="Accent color (hex)" {...field} value={field.value || ''} placeholder="#135FA8" />
                    )} />
                  </div>
                  <Controller name="uiWelcomeTitle" control={control} render={({ field }) => (
                    <Input label="Welcome title" {...field} value={field.value || ''} placeholder="Start the conversation" />
                  )} />
                  <Controller name="uiWelcomeText" control={control} render={({ field }) => (
                    <TextArea label="Welcome text" value={field.value || ''} onChange={field.onChange} rows={2}
                      placeholder="What this co-pilot helps with (shown before the first message)." />
                  )} />
                  <Controller name="uiStarters" control={control} render={({ field }) => (
                    <TextArea label="Starter questions (one per line)" value={field.value || ''} onChange={field.onChange} rows={3}
                      placeholder={'What can you help me with?\nShow me the latest updates'} />
                  )} />
                  <div className="flex flex-wrap gap-4">
                    <Controller name="uiShowSources" control={control} render={({ field }) => (
                      <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                        <input type="checkbox" checked={!!field.value} onChange={(e) => field.onChange(e.target.checked)} />
                        Show source chips
                      </label>
                    )} />
                    <Controller name="uiShowFeedback" control={control} render={({ field }) => (
                      <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                        <input type="checkbox" checked={!!field.value} onChange={(e) => field.onChange(e.target.checked)} />
                        Show feedback (thumbs)
                      </label>
                    )} />
                  </div>
                </div>

                <Controller name="onlineEvalEnabled" control={control} render={({ field }) => (
                  <label className="mt-1 flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                    <input type="checkbox" checked={!!field.value} onChange={(e) => field.onChange(e.target.checked)} />
                    Score a sample of live turns (online eval)
                  </label>
                )} />
                {onlineEvalEnabled && (
                  <div className="ui-inset flex flex-col gap-3 p-3">
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                      <Controller name="sampleRate" control={control} render={({ field }) => (
                        <Input label="Sample rate (0–1)" {...field} value={field.value || ''} type="number" placeholder="0.1" />
                      )} />
                      <Controller name="judgeModelRef" control={control} render={({ field }) => (
                        <FormSelect label="Judge model" name={field.name} value={field.value || ''} options={modelOptions}
                          onChange={(e: any) => field.onChange(e.target ? e.target.value : e)} showClear filter />
                      )} />
                    </div>
                    <Controller name="metrics" control={control} render={({ field }) => (
                      <div className="flex flex-col gap-1.5">
                        <span className="text-xs font-medium text-gray-600 dark:text-gray-300">Metrics</span>
                        <div className="flex flex-wrap gap-4">
                          {ONLINE_EVAL_METRICS.map((m) => {
                            const selected = (field.value || []).includes(m);
                            return (
                              <label key={m} className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                                <input type="checkbox" checked={selected}
                                  onChange={(e) => field.onChange(e.target.checked
                                    ? [...(field.value || []), m]
                                    : (field.value || []).filter((x: string) => x !== m))} />
                                {m.replace(/_/g, ' ').toLowerCase()}
                              </label>
                            );
                          })}
                        </div>
                      </div>
                    )} />
                  </div>
                )}
              </>
            )}
            <p className="max-w-prose text-xs text-ink-muted" style={PRETTY}>
              A conversational agent can have no tools. Retrieval, summarisation and online scoring
              all reuse the platform&rsquo;s prompts and judges.
            </p>
          </div>
        </details>

        <div className="flex justify-end gap-2">
          <Button type="button" label="Cancel" severity="secondary" outlined className="ui-press"
            onClick={() => handleNavigate('/agents')} />
          <Button type="submit" label={isEditMode ? 'Update Agent' : 'Create Agent'} icon="pi pi-check"
            className="ui-press" loading={mutation.isPending} />
        </div>
      </form>

      <DiscardChangesDialog visible={showLeaveDialog} onStay={cancelLeave} onLeave={confirmLeave} />
    </div>
  );
};

export default AgentForm;
